import { useEffect, useMemo } from 'react';
import { Routes, Navigate, Route, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { Filters, useFilters } from '@components/filters';

import {
  useFilterApplicationAssignee,
  useFilterApplicationDates,
  useFilterApplicationLabels,
  useFilterApplicationLanguage,
  useFilterApplicationNationalRegistry,
  useFilterApplicationPersonalDetails,
  useFilterApplicationQuestion,
  useFilterApplicationRating,
  useFilterApplicationStage,
  useFilterApplicationStatus,
  useFilterApplicationUnreadActivity,
} from 'features/filters';
import { useFilterApplicationADManager } from 'integrations/adManager/filters';
import { useFilterApplicationHireflix } from 'integrations/hireflix/filters';
import { JourneysTab } from 'integrations/journeys/job-settings/journeys-tab';
import { useShouldShowJourneysBanner } from 'integrations/journeys/job-triggers/hooks';
import { JourneysBanner } from 'integrations/journeys/job-triggers/journeys-banner';
import { useStore } from 'models/Provider';

import { Access } from './Access';
import { Applicants } from './Applicants';
import { Info } from './Info';
import Integrations from './Integrations';
import { JobHeader } from './JobHeader';
import { Pipeline } from './pipeline';
import { Promote } from './Promote';
import { Statistics } from './Statistics';
import { JobPage } from './styles';

function useFilterConfig(job) {
  const personalDetails = useFilterApplicationPersonalDetails();
  const question = useFilterApplicationQuestion(job);
  const status = useFilterApplicationStatus();
  const rating = useFilterApplicationRating(job);
  const labels = useFilterApplicationLabels();
  const stage = useFilterApplicationStage(job);
  const language = useFilterApplicationLanguage(job);
  const assignee = useFilterApplicationAssignee(job);
  const dates = useFilterApplicationDates();
  const activity = useFilterApplicationUnreadActivity();
  const nationalRegistry = useFilterApplicationNationalRegistry();
  const hireflix = useFilterApplicationHireflix(job);
  const adManager = useFilterApplicationADManager(job);

  return useMemo(() => {
    return [
      [personalDetails, question, status, rating, labels, stage, language],
      [assignee, dates],
      [activity],
      [nationalRegistry, hireflix, adManager],
    ];
  }, [
    personalDetails,
    question,
    status,
    rating,
    labels,
    stage,
    language,
    assignee,
    dates,
    activity,
    nationalRegistry,
    hireflix,
    adManager,
  ]);
}

const JobWrap = observer(() => {
  const { jobID } = useParams();
  const { jobStore } = useStore();

  const job = jobStore.items.get(jobID);

  useEffect(() => {
    if (job) {
      !job.detailLoaded && job.load();
    } else {
      jobStore.loadJob(jobID);
    }
  }, [job, jobID, jobStore]);

  if (!job || !job.detailLoaded || job.settings.state !== 'loaded') return null;

  return <Job job={job} />;
});

const Job = observer(({ job }) => {
  const { t } = useStore();

  const filters = useFilters({
    filterConfigs: useFilterConfig(job),
    onFilterChange: job.settings.setFilters,
    onFilterClear: job.settings.clearSearchValue,
    initialState: {
      isOpen: true,
      conjunction: job.settings.filters.conjunction,
      appliedFilters: job.settings.filters.appliedFilters,
    },
  });

  const showingCountText = job.loadingApplicationsCount
    ? t('filters.loading')
    : t('applicants.filters.showing', job.numFilteredApplications, job.numApplications);

  const shouldShowJourneysBanner = useShouldShowJourneysBanner(job.id);
  // Not memoised on purpose: inside an observer, re-reading stage.name every render
  // is what keeps the panel current when a stage is renamed. Mirrors JobHeader.
  const journeyStages = (job.jobStages ?? []).map((stage) => ({
    id: stage.id,
    name: stage.name,
    dynamicStage: stage.dynamicStage,
  }));

  const filtersEl = (
    <Filters
      getConjunctionProps={(conj) => ({
        allowSwitch: true,
        label: t('filters.conj.' + conj),
      })}
      addFilterText={t('filters.add')}
      clearFiltersText={t('filters.clear')}
      showingCountText={showingCountText}
      {...filters}
    />
  );

  return (
    <JobPage>
      <JobHeader job={job} filters={filters} />
      <Routes>
        <Route path="pipeline" element={filtersEl} />
        <Route path="list" element={filtersEl} />
      </Routes>
      <Routes>
        <Route
          path="list"
          element={
            <Applicants
              job={job}
              queryData={filters.queryData}
              conjunction={filters.state.conjunction}
            />
          }
        />
        <Route
          path="pipeline"
          element={
            <>
              {shouldShowJourneysBanner && (
                <JourneysBanner jobId={job.id} jobStages={journeyStages} />
              )}
              <Pipeline
                job={job}
                queryData={filters.queryData}
                conjunction={filters.state.conjunction}
                filtersOpen={filters.state.isOpen}
                hasJourneysBanner={shouldShowJourneysBanner}
              />
            </>
          }
        />
        <Route path="info/*" element={<Info job={job} />} />
        <Route path="access" element={<Access jobId={job.id} />} />
        <Route path="statistics" element={<Statistics job={job} />} />
        <Route path="integrations/*" element={<Integrations job={job} />} />
        <Route path="promote/*" element={<Promote job={job} />} />
        <Route path="journeys" element={<JourneysTab jobId={job.id} />} />
        <Route path="*" element={<Navigate to={{ pathname: job.pipelineUrl }} replace />} />
      </Routes>
    </JobPage>
  );
});

export default JobWrap;
